import {
	UpdateChartWidgetForDashboardParams,
	BuildDashboardContext,
} from './types';
import { buildChartEngine } from '../build-chart/build-chart-engine';
import { getBaseChartSummary } from '../build-chart/build-chart-service';
import {
	BuildChartContext,
	ExtendedChartWidgetProps,
} from '../build-chart/types';
import { isChartWidgetProps } from '@sisense/sdk-ui';
import { devDebug } from '@/utils/dev-debug';

export const updateChartWidgetForDashboard = async (
	{ dashboardId, widgetId, userPrompt }: UpdateChartWidgetForDashboardParams,
	context: BuildDashboardContext,
) => {
	devDebug('toolCall', {
		tool: 'updateChartWidgetForDashboard',
		dashboardId,
		widgetId,
		userPrompt,
	});

	const {
		toolCallId,
		dataSourceTitle,
		retrieveDashboard,
		saveDashboard,
		httpClient,
		openAIClient,
		isNlqV3Enabled,
	} = context;
	if (!toolCallId) {
		throw new Error('Error: Invalid tool call ID. Please try again.');
	}

	const dashboardProps = retrieveDashboard?.(dashboardId);
	if (!dashboardProps) {
		throw new Error('Dashboard not found.');
	}

	let widgetIndex = dashboardProps.widgets.findIndex(
		(widget) => widget.id === widgetId,
	);
	if (widgetIndex === -1) {
		devDebug('toolCall', {
			tool: 'updateChartWidgetForDashboard',
			phase: 'widgetIdFallback',
			note: 'Widget id not found; using last chart widget on dashboard',
			dashboardId,
			widgetId,
			userPrompt,
			fallbackWidgetIndex: dashboardProps.widgets.length - 1,
		});
		widgetIndex = dashboardProps.widgets.length - 1;
	}

	const widgetProps = dashboardProps.widgets[widgetIndex];

	if (!isChartWidgetProps(widgetProps)) {
		throw new Error('Widget is not a chart widget.');
	}

	// Use the resolved widget ID after fallback selection
	const resolvedWidgetId = widgetProps.id ?? widgetId;
	let savedChartWidgetProps = widgetProps;

	const chartSummaries = [
		{
			message: 'This is the current chart widget',
			createdAt: new Date().toISOString(),
			chartId: resolvedWidgetId,
			dataSourceTitle,
			chartState: getBaseChartSummary(savedChartWidgetProps),
		},
	];

	const buildChartContext: BuildChartContext = {
		toolCallId,
		dataSourceTitle,
		chartSummaries,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		retrieveChart: (_: string) => savedChartWidgetProps,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		saveChart: (_: string, chartWidgetProps: ExtendedChartWidgetProps) => {
			// Preserve the resolved widget ID to maintain dashboard layout references
			const updatedWidgetProps: ExtendedChartWidgetProps = {
				...chartWidgetProps,
				id: resolvedWidgetId,
			};
			dashboardProps.widgets[widgetIndex] = updatedWidgetProps;
			savedChartWidgetProps = updatedWidgetProps;
		},
		isNlqV3Enabled,
		httpClient,
		openAIClient,
	};

	const chartSummary = await buildChartEngine(
		{ dataSourceTitle, userPrompt, priorChartId: resolvedWidgetId },
		buildChartContext,
	);

	if (chartSummary.isError) {
		throw new Error(chartSummary.message);
	}

	saveDashboard?.(dashboardProps);

	return {
		message:
			'Chart widget updated successfully. Click "Save" to persist changes to My Dashboards.',
	};
};
